/* ==========================================================================
   Egoss Atelier — Collection page load-more + scroll restore + back-to-top
   ==========================================================================
   Defines two custom elements:

     <atelier-load-more
       data-section-id="<id>"
       data-product-list-id="<id>">
       <button data-load-more-button data-current-page="N" [data-next-url="..."]>LOAD MORE</button>
       <p data-load-more-end hidden>END OF COLLECTION</p>
     </atelier-load-more>

     <atelier-back-to-top>
       <button>↑</button>
     </atelier-back-to-top>

   Behavior summary:
   • Load More click → fetch next page HTML → extract <product-list> children →
     append to current grid → pushState('?page=N+1') → update / hide button.
   • Click on any product link inside the grid → save scrollY + currentPage
     to sessionStorage keyed by pathname + active filter/sort params.
   • On collection load: if URL has ?page=N>1 OR sessionStorage has saved
     state, backfill pages 1..N-1 (silent prepend) + forward-load to savedPage,
     then scroll to saved position.
   • Back to Top: fixed bottom-right circle, appears after >1 viewport scroll,
     smooth scroll on click, respects prefers-reduced-motion.

   No build step. No deps. Vanilla web components, ES modules-free.
   ========================================================================== */

(function () {
  'use strict';

  // --------------------------------------------------------------------------
  // <atelier-load-more>
  // --------------------------------------------------------------------------
  class AtelierLoadMore extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('[data-load-more-button]');
      this.endNote = this.querySelector('[data-load-more-end]');
      if (!this.button) return;

      this.button.addEventListener('click', () => this.loadNext());

      // Disable browser's scroll restoration so we own it.
      if ('scrollRestoration' in history) history.scrollRestoration = 'manual';

      // Restore state from any prior PDP visit (or honor a bookmarked ?page=N URL).
      this.maybeRestoreState();
    }

    get sectionId() { return this.getAttribute('data-section-id'); }
    get productList() { return document.getElementById(this.getAttribute('data-product-list-id')); }
    get nextUrl() { return this.button.getAttribute('data-next-url'); }
    get currentPage() {
      return parseInt(this.button.getAttribute('data-current-page'), 10) || 1;
    }

    storageKey() {
      const url = new URL(window.location.href);
      const params = Array.from(url.searchParams.entries())
        .filter(([k]) => k !== 'page')
        .sort();
      return url.pathname + '?' + params.map(([k, v]) => k + '=' + v).join('&');
    }

    /**
     * Remove `reveal-on-scroll` attributes from each element and its descendants.
     *
     * Prestige's intersection observer (reveal-on-scroll.js) only tracks elements
     * present at initial page load, so dynamically appended product cards would
     * stay stuck at opacity 0 forever. Stripping the attribute before append
     * makes the cards render as normal, opaque elements — the reveal animation
     * is skipped for load-more content, which is fine because the user just
     * intentionally clicked to reveal them.
     */
    stripRevealAttrs(elements) {
      elements.forEach((el) => {
        el.removeAttribute('reveal-on-scroll');
        el.querySelectorAll('[reveal-on-scroll]').forEach((inner) => {
          inner.removeAttribute('reveal-on-scroll');
        });
      });
    }

    async loadNext({ silent = false } = {}) {
      const nextUrl = this.nextUrl;
      if (!nextUrl) return;

      if (!silent) {
        this.button.setAttribute('disabled', '');
        this.button.dataset.originalText = this.button.textContent;
        this.button.textContent = 'LOADING…';
      }

      try {
        const fetchUrl = new URL(nextUrl, window.location.origin);
        fetchUrl.searchParams.set('section_id', this.sectionId);

        const res = await fetch(fetchUrl.toString(), {
          headers: { 'Accept': 'text/html' },
        });
        if (!res.ok) throw new Error('fetch failed: ' + res.status);
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');

        // Append new product cards to the bottom of the current grid.
        const newList = doc.querySelector('product-list');
        if (newList && this.productList) {
          const cards = Array.from(newList.children);
          this.stripRevealAttrs(cards);
          const frag = document.createDocumentFragment();
          cards.forEach((c) => frag.appendChild(c));
          this.productList.appendChild(frag);
        }

        // Update URL with the page we just loaded — only on user-initiated clicks.
        if (!silent) {
          const cleanUrl = new URL(nextUrl, window.location.origin);
          cleanUrl.searchParams.delete('section_id');
          history.pushState({ atelierLoadMore: true }, '', cleanUrl.toString());
        }

        // Pull fresh next-page data from the freshly-fetched response.
        const newLoadMore = doc.querySelector('atelier-load-more');
        const newButton = newLoadMore && newLoadMore.querySelector('[data-load-more-button]');
        const newNextUrl = newButton && newButton.getAttribute('data-next-url');
        const newCurrentPage = newButton && newButton.getAttribute('data-current-page');

        if (newCurrentPage) {
          this.button.setAttribute('data-current-page', newCurrentPage);
        }

        if (newNextUrl) {
          this.button.setAttribute('data-next-url', newNextUrl);
          if (!silent) {
            this.button.removeAttribute('disabled');
            this.button.textContent = this.button.dataset.originalText || 'LOAD MORE';
          }
        } else {
          // Last page — hide button, show end note.
          this.button.removeAttribute('data-next-url');
          this.button.hidden = true;
          if (this.endNote) this.endNote.hidden = false;
        }
      } catch (err) {
        console.error('[atelier-load-more]', err);
        if (!silent) {
          this.button.removeAttribute('disabled');
          this.button.textContent = this.button.dataset.originalText || 'LOAD MORE';
        }
      }
    }

    /**
     * Backfill pages [start..end] inclusive — silent fetch + prepend in order
     * so the visible grid reads page1, page2, ..., currently-rendered-page.
     */
    async backfillPages(start, end) {
      const collected = [];
      for (let p = start; p <= end; p++) {
        try {
          const url = new URL(window.location.href);
          url.searchParams.set('page', String(p));
          url.searchParams.set('section_id', this.sectionId);
          const res = await fetch(url.toString(), { headers: { 'Accept': 'text/html' } });
          if (!res.ok) continue;
          const html = await res.text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const list = doc.querySelector('product-list');
          if (!list) continue;
          Array.from(list.children).forEach((c) => collected.push(c));
        } catch (err) {
          console.error('[atelier-load-more] backfill error page ' + p, err);
        }
      }
      if (collected.length && this.productList) {
        this.stripRevealAttrs(collected);
        const frag = document.createDocumentFragment();
        collected.forEach((c) => frag.appendChild(c));
        this.productList.insertBefore(frag, this.productList.firstChild);
      }
    }

    async maybeRestoreState() {
      const url = new URL(window.location.href);
      const urlPage = parseInt(url.searchParams.get('page'), 10) || 1;
      const key = this.storageKey();
      const savedScroll = parseInt(sessionStorage.getItem('atelier-scroll:' + key), 10);
      const savedPage = parseInt(sessionStorage.getItem('atelier-page:' + key), 10);

      // Bookmark / direct nav with ?page=N>1 → backfill prior pages so the grid
      // reads continuously from page 1.
      if (urlPage > 1) {
        await this.backfillPages(1, urlPage - 1);
      }

      // Saved page is further than what's on screen → forward-load via loadNext.
      if (savedPage && savedPage > urlPage) {
        // Cap iterations so a corrupted storage value can't loop forever.
        let safety = 50;
        while (this.currentPage < savedPage && this.nextUrl && safety-- > 0) {
          await this.loadNext({ silent: true });
        }
      }

      // Scroll to the saved position once everything is in the DOM.
      if (savedScroll && savedScroll > 0) {
        requestAnimationFrame(() => {
          window.scrollTo({ top: savedScroll, behavior: 'auto' });
        });
      }

      // One-shot — clear so a refresh doesn't replay the restore.
      sessionStorage.removeItem('atelier-scroll:' + key);
      sessionStorage.removeItem('atelier-page:' + key);

      this.attachProductCardListeners();
    }

    attachProductCardListeners() {
      const list = this.productList;
      if (!list) return;
      // Single delegated listener — captures clicks on any link to a product.
      list.addEventListener('click', (e) => {
        const link = e.target.closest('a[href*="/products/"]');
        if (!link) return;
        const key = this.storageKey();
        try {
          sessionStorage.setItem('atelier-scroll:' + key, String(window.scrollY));
          sessionStorage.setItem('atelier-page:' + key, String(this.currentPage));
        } catch (_) {
          /* sessionStorage unavailable (private mode etc.) — ignore */
        }
      }, true);
    }
  }

  if (!customElements.get('atelier-load-more')) {
    customElements.define('atelier-load-more', AtelierLoadMore);
  }

  // --------------------------------------------------------------------------
  // <atelier-back-to-top> — moved to assets/egoss-smart-header.js so it can
  // register site-wide (not only on collection pages). Custom element name
  // stays the same; the <atelier-back-to-top> markup lives in
  // snippets/egoss-back-to-top.liquid, rendered from layout/theme.liquid.
  // --------------------------------------------------------------------------
})();
